import { useState, useEffect, useCallback } from "react";
import { Epic, Story, Sprint } from "../types";
import {
  SecureJiraApiService,
  JiraConfig,
  JiraApiResponse,
} from "../services/secureJiraApi";

interface JiraDataState {
  epics: Epic[];
  stories: Story[];
  sprints: Sprint[];
  loading: boolean;
  error: string | null;
  connected: boolean;
}

interface UseJiraDataReturn extends JiraDataState {
  connect: (config: {
    baseUrl: string;
    email: string;
    projectKey: string;
  }) => Promise<void>;
  submitApiToken: (apiToken: string) => Promise<void>;
  disconnect: () => void;
  refreshData: () => Promise<void>;
  logWork: (
    issueKey: string,
    timeSpent: string,
    comment?: string,
  ) => Promise<void>;
  updateIssueStatus: (issueKey: string, status: string) => Promise<void>;
}

export const useJiraData = (): UseJiraDataReturn => {
  const [state, setState] = useState<JiraDataState>({
    epics: [],
    stories: [],
    sprints: [],
    loading: false,
    error: null,
    connected: false,
  });

  const [jiraService] = useState(() => new SecureJiraApiService());

  const connect = useCallback(
    async (config: { baseUrl: string; email: string; projectKey: string }) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        // Connect through secure API proxy
        const result = await jiraService.connect(config);

        if (!result.success) {
          throw new Error(result.error?.message || "Failed to connect to Jira");
        }

        // Check if server needs API token
        if (result.data?.needsToken) {
          throw new Error(
            "API token required. Please provide your API token securely.",
          );
        }

        setState((prev) => ({
          ...prev,
          connected: true,
          loading: false,
          error: null,
        }));

        // Load initial data
        await loadData();
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to connect to Jira",
          connected: false,
        }));
      }
    },
    [jiraService],
  );

  const submitApiToken = useCallback(
    async (apiToken: string) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const result = await jiraService.submitApiToken(apiToken);

        if (!result.success) {
          throw new Error(
            result.error?.message || "Failed to authenticate with API token",
          );
        }

        setState((prev) => ({
          ...prev,
          connected: true,
          loading: false,
          error: null,
        }));

        // Load initial data after successful authentication
        await loadData();
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to authenticate API token",
        }));
      }
    },
    [jiraService],
  );

  const disconnect = useCallback(async () => {
    try {
      await jiraService.disconnect();
    } catch (error) {
      console.error("Error during disconnect:", error);
    }

    setState({
      epics: [],
      stories: [],
      sprints: [],
      loading: false,
      error: null,
      connected: false,
    });
  }, [jiraService]);

  const loadData = async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // First check if we're still connected
      const statusResult = await jiraService.getStatus();
      if (!statusResult.success || !statusResult.data?.connected) {
        throw new Error("Not connected to Jira");
      }

      // Get boards first to determine the primary board
      const boardsResult = await jiraService.getBoards(
        statusResult.data.project.projectKey,
      );
      if (
        !boardsResult.success ||
        !boardsResult.data ||
        boardsResult.data.length === 0
      ) {
        throw new Error("No Scrum boards found for this project");
      }

      const boardId = boardsResult.data[0].id;

      // Load data in parallel
      const [epicsResult, sprintsResult] = await Promise.all([
        jiraService.getEpics(),
        jiraService.getSprints(boardId),
      ]);

      if (!epicsResult.success || !epicsResult.data) {
        throw new Error("Failed to fetch epics");
      }

      if (!sprintsResult.success || !sprintsResult.data) {
        throw new Error("Failed to fetch sprints");
      }

      const epics = epicsResult.data;
      const sprints = sprintsResult.data;

      // Load stories for each epic and sprint
      const epicStoriesPromises = epics.map((epic) =>
        jiraService.getEpicStories(epic.key),
      );

      const sprintStoriesPromises = sprints.map((sprint) =>
        jiraService.getSprintIssues(boardId, sprint.id),
      );

      const [epicStoriesResults, sprintStoriesResults] = await Promise.all([
        Promise.all(epicStoriesPromises),
        Promise.all(sprintStoriesPromises),
      ]);

      // Combine all stories and remove duplicates
      const allStories = new Map<string, Story>();

      epicStoriesResults.forEach((result, index) => {
        if (result.success && result.data) {
          epics[index].stories = result.data;
          result.data.forEach((story) => allStories.set(story.id, story));
        }
      });

      sprintStoriesResults.forEach((result, index) => {
        if (result.success && result.data) {
          const stories = result.data;
          sprints[index].stories = stories.map((s) => s.id);
          sprints[index].totalPoints = stories.reduce(
            (sum, s) => sum + s.storyPoints,
            0,
          );
          sprints[index].completedPoints = stories
            .filter((s) => s.status === "done")
            .reduce((sum, s) => sum + s.storyPoints, 0);
          sprints[index].velocity =
            sprints[index].status === "completed"
              ? sprints[index].completedPoints
              : 0;

          stories.forEach((story) => allStories.set(story.id, story));
        }
      });

      setState((prev) => ({
        ...prev,
        epics,
        stories: Array.from(allStories.values()),
        sprints,
        loading: false,
      }));
    } catch (error) {
      console.error("Failed to load Jira data:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error ? error.message : "Failed to load Jira data",
      }));
    }
  };

  const refreshData = useCallback(async () => {
    if (!state.connected) return;
    await loadData();
  }, [state.connected]);

  const logWork = useCallback(
    async (issueKey: string, timeSpent: string, comment?: string) => {
      if (!state.connected) throw new Error("Not connected to Jira");

      try {
        const result = await jiraService.logWork(issueKey, timeSpent, comment);
        if (!result.success) {
          throw new Error(result.error?.message || "Failed to log work");
        }
        await refreshData(); // Refresh to get updated time tracking
      } catch (error) {
        throw new Error(
          `Failed to log work: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    [jiraService, refreshData, state.connected],
  );

  const updateIssueStatus = useCallback(
    async (issueKey: string, status: string) => {
      if (!state.connected) throw new Error("Not connected to Jira");

      try {
        // This would need to map status to transition IDs
        // For now, we'll just refresh the data
        await refreshData();
      } catch (error) {
        throw new Error(
          `Failed to update issue: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
    [jiraService, refreshData, state.connected],
  );

  // Check connection status on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const result = await jiraService.getStatus();
        if (result.success && result.data?.connected) {
          setState((prev) => ({ ...prev, connected: true }));
          // Optionally load data if connected
          // await loadData();
        }
      } catch (error) {
        console.error("Failed to check connection status:", error);
      }
    };

    checkConnection();
  }, [jiraService]);

  return {
    ...state,
    connect,
    submitApiToken,
    disconnect,
    refreshData,
    logWork,
    updateIssueStatus,
  };
};
